import { apiBaseUrl } from './api';

/**
 * Stream Feeds Utility
 *
 * Manages Stream Feeds operations via backend API calls
 * All Stream Feeds SDK operations happen on the server for security
 */

let currentUserId = null;
let isInitialized = false;
let initializePromise = null;

const FEED_CACHE_DURATION = 30 * 1000;
const followingFeedCache = new Map();
const followingFeedRequests = new Map();

/**
 * Initialize Stream Feeds for a user
 * This just validates the user can connect - actual SDK is on backend
 * @param {string} userId - User ID
 * @param {string} userName - User name (optional, for display)
 * @returns {Promise<boolean>} - Success status
 */
export const initializeStreamFeeds = async (userId, userName = '') => {
  try {
    // If already initialized for this user, return success
    if (isInitialized && currentUserId === userId) {
      return true;
    }

    if (initializePromise && currentUserId === userId) {
      return initializePromise;
    }

    currentUserId = userId;

    // Validate we can get a token from backend
    initializePromise = fetch(`${apiBaseUrl()}/api/stream-feeds/token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        userName,
      }),
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to get Stream Feeds token: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !data.token) {
          throw new Error('Invalid token response from server');
        }

        currentUserId = userId;
        isInitialized = true;

        return true;
      })
      .finally(() => {
        initializePromise = null;
      });

    return initializePromise;
  } catch (error) {
    initializePromise = null;
    currentUserId = null;
    console.error('❌ Failed to initialize Stream Feeds:', error);
    throw error;
  }
};

/**
 * Disconnect from Stream Feeds
 */
export const disconnectStreamFeeds = async () => {
  isInitialized = false;
  currentUserId = null;
  initializePromise = null;
};

/**
 * Check if Stream Feeds is initialized
 * @returns {boolean}
 */
export const isStreamFeedsReady = () => {
  return isInitialized;
};

/**
 * Create a post activity via backend API
 * @param {Object} postData - Post data
 * @returns {Promise<Object>} - Created activity
 */
export const createPost = async postData => {
  if (!isInitialized || !currentUserId) {
    throw new Error('Stream Feeds not initialized');
  }

  const { crew_id, content, image_url, ...metadata } = postData;

  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/posts`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: currentUserId,
        crew_id,
        content,
        image_url,
        ...metadata,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create post' }));
      throw new Error(error.message || 'Failed to create post');
    }

    const data = await response.json();
    if (!data.success || data.moderation_rejected) {
      throw new Error(data.message || 'This post was not created.');
    }
    return data.activity;
  } catch (error) {
    console.error('❌ Failed to create post:', error);
    throw error;
  }
};

/**
 * Get user's posts via backend API
 * @param {string} userId - User ID
 * @param {number} limit - Number of posts to fetch
 * @returns {Promise<Array>} - Array of activities
 */
export const getUserPosts = async (userId, limit = 25) => {
  try {
    const params = new URLSearchParams({
      feedType: 'user',
      feedId: userId,
      limit: limit.toString(),
      withReactions: 'true',
    });

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/feed?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user posts: ${response.status}`);
    }

    const data = await response.json();
    return data.activities || [];
  } catch (error) {
    console.error('❌ Failed to get user posts:', error);
    throw error;
  }
};

/**
 * Get user's notifications via backend API
 * @param {string} userId - User ID
 * @param {Object} options - { markSeen: boolean, markRead: boolean }
 * @returns {Promise<Object>} - { activities, unread, unseen }
 */
export const getUserNotifications = async (userId, options = {}) => {
  try {
    const params = new URLSearchParams({
      feedType: 'notification',
      feedId: userId,
      limit: '25',
    });

    if (options.markSeen) params.append('markSeen', 'true');
    if (options.markRead) params.append('markRead', 'true');

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/feed?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to get notifications:', error);
    throw error;
  }
};

/**
 * Get crew's posts (timeline) via backend API
 * @param {string} crewId - Crew ID
 * @param {number} limit - Number of posts to fetch
 * @returns {Promise<Array>} - Array of activities
 */
export const getCrewPosts = async (crewId, limit = 25) => {
  try {
    const params = new URLSearchParams({
      feedType: 'crew',
      feedId: crewId,
      limit: limit.toString(),
      withReactions: 'true',
    });

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/feed?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch crew posts: ${response.status}`);
    }

    const data = await response.json();
    return data.activities || [];
  } catch (error) {
    console.error('❌ Failed to get crew posts:', error);
    throw error;
  }
};

/**
 * Get aggregated timeline feed (all crews user follows) via backend API
 * @param {string} userId - User ID
 * @param {number} limit - Number of posts to fetch
 * @returns {Promise<Array>} - Array of activities
 */
export const getTimelineFeed = async (userId, limit = 25) => {
  try {
    const params = new URLSearchParams({
      feedType: 'timeline',
      feedId: userId,
      limit: limit.toString(),
      withReactions: 'true',
    });

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/feed?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch timeline: ${response.status}`);
    }

    const data = await response.json();
    return data.activities || [];
  } catch (error) {
    console.error('❌ Failed to get timeline feed:', error);
    throw error;
  }
};

export const getFollowingUsers = async (userId, limit = 100) => {
  const params = new URLSearchParams({ userId, limit: String(limit) });
  const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/following?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch following: ${response.status}`);
  }

  const data = await response.json();
  return data.users || [];
};

export const getFollowerUsers = async (userId, limit = 100) => {
  const params = new URLSearchParams({ userId, limit: String(limit) });
  const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/followers?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch followers: ${response.status}`);
  }

  const data = await response.json();
  return data.users || [];
};

export const getFollowStatus = async (userId, targetUserId) => {
  const params = new URLSearchParams({ userId, targetUserId });
  const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/follow-status?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch follow status: ${response.status}`);
  }

  const data = await response.json();
  return !!data.isFollowing;
};

export const followUser = async (userId, targetUserId) => {
  const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/follow`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, targetUserId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to follow user' }));
    throw new Error(error.message || 'Failed to follow user');
  }

  return response.json();
};

export const unfollowUser = async (userId, targetUserId) => {
  const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/unfollow`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, targetUserId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to unfollow user' }));
    throw new Error(error.message || 'Failed to unfollow user');
  }

  return response.json();
};

export const getFollowingFeed = async ({ userId, crewId = null, limit = 25 }) => {
  const cacheKey = JSON.stringify({ userId, crewId, limit });
  const cachedEntry = followingFeedCache.get(cacheKey);

  if (
    cachedEntry &&
    cachedEntry.activities &&
    Date.now() - cachedEntry.timestamp < FEED_CACHE_DURATION
  ) {
    return cachedEntry.activities;
  }

  if (followingFeedRequests.has(cacheKey)) {
    return followingFeedRequests.get(cacheKey);
  }

  const params = new URLSearchParams({ userId, limit: String(limit) });
  if (crewId) params.append('crewId', crewId);

  const requestPromise = fetch(`${apiBaseUrl()}/api/stream-feeds/following-feed?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch following feed: ${response.status}`);
      }

      const data = await response.json();
      const activities = data.activities || [];
      followingFeedCache.set(cacheKey, {
        activities,
        timestamp: Date.now(),
      });
      return activities;
    })
    .finally(() => {
      followingFeedRequests.delete(cacheKey);
    });

  followingFeedRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

/**
 * Add a reaction (like, love, etc.) to a post via backend API
 * @param {string} activityId - Activity ID to react to
 * @param {string} reactionType - Type of reaction (like, love, fire, etc.)
 * @param {string} text - Optional text (for comments)
 * @param {string} userName - Optional user name
 * @param {Object} data - Optional custom data (for parent_id in replies)
 * @param {string[]} mentionedUserIds - Optional mentioned user IDs
 * @returns {Promise<Object>} - Reaction response
 */
export const addReaction = async (
  activityId,
  reactionType = 'like',
  text = '',
  userName = '',
  data = null,
  mentionedUserIds = []
) => {
  if (!isInitialized || !currentUserId) {
    console.error('❌ Stream Feeds not initialized:', { isInitialized, currentUserId });
    throw new Error('Stream Feeds not initialized');
  }

  try {
    const requestBody = {
      userId: currentUserId,
      activityId,
      reactionType,
      text: text || undefined,
      userName: userName || undefined,
      mentionedUserIds: mentionedUserIds?.length ? mentionedUserIds : undefined,
    };

    // Include custom data if provided (e.g., parent_id for replies)
    if (data) {
      requestBody.data = data;
    }

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/reactions`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to add reaction' }));
      throw new Error(error.message || 'Failed to add reaction');
    }

    const result = await response.json();
    const reaction = result.reaction || result;
    return reaction;
  } catch (error) {
    console.error('❌ Failed to add reaction:', error);
    throw error;
  }
};

/**
 * Remove a reaction from a post via backend API
 * @param {string} reactionId - Reaction ID to remove
 * @returns {Promise<void>}
 */
export const removeReaction = async reactionId => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/reactions/${reactionId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reactionId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to remove reaction' }));
      throw new Error(error.message || 'Failed to remove reaction');
    }
  } catch (error) {
    console.error('❌ Failed to remove reaction:', error);
    throw error;
  }
};

/**
 * Follow a crew (adds crew feed to user's timeline)
 * Note: This would need a backend endpoint to use Stream SDK
 * For now, this is a placeholder
 * @param {string} userId - User ID
 * @param {string} crewId - Crew ID to follow
 * @returns {Promise<void>}
 */
export const followCrew = async (userId, crewId) => {
  // TODO: Implement backend endpoint for following crews
  return Promise.resolve();
};

/**
 * Unfollow a crew
 * @param {string} userId - User ID
 * @param {string} crewId - Crew ID to unfollow
 * @returns {Promise<void>}
 */
export const unfollowCrew = async (userId, crewId) => {
  // TODO: Implement backend endpoint for unfollowing crews
  return Promise.resolve();
};

/**
 * Add a comment to a post (using Stream Feeds reactions with kind="comment")
 * @param {string} activityId - Activity ID (post) to comment on
 * @param {string} commentText - Comment text
 * @param {string} parentCommentId - Optional parent comment ID for replies
 * @returns {Promise<Object>} - Created comment reaction
 */
export const addComment = async (activityId, commentText, parentCommentId = null) => {
  try {
    const requestBody = {
      activityId,
      kind: 'comment',
      text: commentText,
    };

    // If this is a reply to a comment, include parent_id in data
    if (parentCommentId) {
      requestBody.data = {
        parent_id: parentCommentId,
      };
    }

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/reactions`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add comment: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('❌ Failed to add comment:', error);
    throw error;
  }
};

/**
 * Get comments for a post (using Stream Feeds reactions with kind="comment")
 */
export const getComments = async activityId => {
  try {
    const params = new URLSearchParams({
      activityId,
      kind: 'comment',
    });

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/reactions?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch comments: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('❌ Failed to fetch comments:', error);
    throw error;
  }
};

export const getReactions = async (activityId, kind = null) => {
  try {
    const params = new URLSearchParams({ activityId });
    if (kind) params.append('kind', kind);

    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/reactions?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch reactions: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    return result.results || result.reactions || [];
  } catch (error) {
    console.error('❌ Failed to fetch reactions:', error);
    throw error;
  }
};

// ─── Repost / Quote ─────────────────────────────────────────────────────────

/**
 * Create a repost (quote-post) as a new activity on the crew feed.
 * @param {Object} params
 * @param {string} params.originalActivityId - The Stream activity ID being quoted
 * @param {string} params.quoteText          - User's commentary
 * @param {string} params.crewId             - The crew this repost belongs to
 * @param {Object} params.originalPost       - The full original post object for embedding
 * @returns {Promise<Object>} The newly created activity
 */
export const createRepost = async ({ originalActivityId, quoteText, crewId, originalPost }) => {
  if (!isInitialized || !currentUserId) {
    throw new Error('Stream Feeds not initialized');
  }

  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/posts/repost`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: currentUserId,
        originalActivityId,
        quoteText,
        crewId,
        originalPost,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to repost' }));
      throw new Error(err.message || 'Failed to repost');
    }

    const data = await response.json();
    return data.activity;
  } catch (error) {
    console.error('❌ Failed to create repost:', error);
    throw error;
  }
};

// ─── Report / Block / Delete ────────────────────────────────────────────────

export const getModerationPreferences = async () => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/moderation/preferences`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ message: 'Failed to load moderation preferences' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to load moderation preferences:', error);
    throw error;
  }
};

export const hidePostPreference = async postId => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/hide`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postId }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to hide post' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to persist hidden post preference:', error);
    throw error;
  }
};

export const muteUserPreference = async targetUserId => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/mute`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to mute user' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to persist muted user preference:', error);
    throw error;
  }
};

export const unmuteUserPreference = async targetUserId => {
  try {
    const response = await fetch(
      `${apiBaseUrl()}/api/stream-feeds/mute/${encodeURIComponent(targetUserId)}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to unmute user' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to remove muted user preference:', error);
    throw error;
  }
};

/**
 * Report (flag) an activity to Stream.
 */
export const reportActivity = async (
  activityId,
  reason = '',
  targetUserId = null,
  details = {}
) => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/report`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        activityId,
        reason,
        targetUserId,
        details,
        userId: currentUserId,
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to report';
      try {
        const errData = await response.json();
        errorMessage = errData.message || errorMessage;
      } catch (e) {
        // Not JSON
        console.error('❌ Backend returned non-JSON error during report:', response.status);
      }
      console.error('❌ Report request failed:', errorMessage);
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to report activity:', error);
    throw error;
  }
};

/**
 * Block a user via Stream.
 */
export const blockUser = async targetUserId => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/block`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to block user' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to block user:', error);
    throw error;
  }
};

export const unblockUser = async targetUserId => {
  try {
    const response = await fetch(
      `${apiBaseUrl()}/api/stream-feeds/block/${encodeURIComponent(targetUserId)}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to unblock user' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to unblock user:', error);
    throw error;
  }
};

/**
 * Delete an activity (only the author should be allowed).
 */
export const deleteActivity = async (activityId, userId = currentUserId, options = {}) => {
  try {
    const response = await fetch(`${apiBaseUrl()}/api/stream-feeds/posts/${activityId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        crewId: options.crewId || null,
        foreignId: options.foreignId || null,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to delete' }));
      throw new Error(err.message);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to delete activity:', error);
    throw error;
  }
};

export default {
  initializeStreamFeeds,
  disconnectStreamFeeds,
  isStreamFeedsReady,
  getModerationPreferences,
  hidePostPreference,
  muteUserPreference,
  unmuteUserPreference,
  createPost,
  getUserPosts,
  getCrewPosts,
  getTimelineFeed,
  addReaction,
  removeReaction,
  followCrew,
  unfollowCrew,
  addComment,
  getComments,
  getReactions,
  createRepost,
  reportActivity,
  blockUser,
  unblockUser,
  deleteActivity,
};
